import { Link } from 'react-router-dom';
import type { ArticleContent } from '@/mocks/article-content';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://itaobuyspread.cfd';

interface ArticleLayoutProps {
  article: ArticleContent;
}

export default function ArticleLayout({ article }: ArticleLayoutProps) {
  const currentUrl = `${siteUrl}/${article.slug === 'cos-e-itaobuy-spreadsheet' ? 'cos-e-itaobuy-spreadsheet' : article.slug}`;

  return (
    <main>
      {/* Sticky Navbar */}
      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-100">
        <div className="w-full px-4 md:px-8">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="flex items-center gap-2 cursor-pointer group">
              <i className="ri-arrow-left-line text-foreground-500 group-hover:text-foreground-900 transition-colors"></i>
              <span className="text-sm font-label font-medium text-foreground-600 group-hover:text-foreground-900 transition-colors">
                Itaobuy Spreadsheet
              </span>
            </Link>
            <div className="flex items-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="hidden sm:inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-foreground-200 text-foreground-600 text-xs font-label font-medium cursor-pointer whitespace-nowrap hover:bg-foreground-100 transition-colors"
              >
                Guida all&apos;acquisto
                <i className="ri-external-link-line"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-4 py-1.5 rounded-full bg-foreground-950 text-background-50 text-xs font-label font-medium cursor-pointer whitespace-nowrap hover:bg-foreground-800 transition-colors"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      </nav>

      <article className="py-10 md:py-14 px-4 md:px-8">
        <div className="max-w-[720px] mx-auto">
          {/* Breadcrumb */}
          <nav className="text-xs text-foreground-400 font-label mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-foreground-600 transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground-600">{article.title.split(':')[0].trim()}</span>
          </nav>

          {/* Category Badge */}
          <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-label font-medium mb-4">
            {article.category}
          </span>

          {/* H1 */}
          <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-4">
            {article.title}
          </h1>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500 font-label mb-8 pb-6 border-b border-background-100">
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i> {article.readTime} di lettura
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i> Aggiornato: {article.date}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-user-line"></i> {article.author}
            </span>
          </div>

          {/* Direct Answer Box */}
          <div className="bg-accent-50/60 border border-accent-200/50 rounded-2xl p-5 mb-10">
            <p className="text-xs font-label font-semibold text-accent-600 mb-2 uppercase tracking-wide">Risposta Rapida</p>
            <p className="text-sm font-label font-semibold text-foreground-900 mb-1">
              Domanda: &ldquo;{article.directAnswer.question}&rdquo;
            </p>
            <p className="text-sm text-foreground-700 leading-relaxed">
              {article.directAnswer.answer}
            </p>
          </div>

          {/* Trust Box */}
          <div className="bg-background-100 border border-background-200 rounded-2xl p-4 mb-10 flex items-start gap-3">
            <i className="ri-shield-check-line text-accent-500 text-xl mt-0.5"></i>
            <p className="text-xs text-foreground-600 leading-relaxed">
              <strong>Questa guida è stata aggiornata e verificata dal nostro team editoriale.</strong> Le informazioni contenute in questo articolo sono state revisionate a {article.date} per garantire accuratezza e affidabilità.
            </p>
          </div>

          {/* Introduction */}
          <section className="mb-10">
            {article.introduction.map((p, i) => (
              <p key={i} className="text-base leading-relaxed text-foreground-700 mb-4" dangerouslySetInnerHTML={{ __html: p }}></p>
            ))}
          </section>

          {/* Sections */}
          {article.sections.map((section, secIdx) => (
            <section key={secIdx} className="mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                {section.title}
              </h2>

              {section.paragraphs.map((p, i) => (
                <p key={i} className="text-base leading-relaxed text-foreground-700 mb-4" dangerouslySetInnerHTML={{ __html: p }}></p>
              ))}

              {/* Numbered Steps */}
              {section.numberedSteps && (
                <div className="space-y-4 mb-6">
                  {section.numberedSteps.map((step, i) => (
                    <div key={i} className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-foreground-950 text-background-50 text-xs flex items-center justify-center font-label font-semibold">{i + 1}</span>
                      <div>
                        <p className="text-sm text-foreground-600">{step}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Table */}
              {section.table && (
                <div className="overflow-x-auto my-6">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="border-b-2 border-foreground-200">
                        {section.table.headers.map((h, i) => (
                          <th key={i} className="text-left py-3 px-4 font-label font-semibold text-foreground-900 text-xs">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {section.table.rows.map((row, i) => (
                        <tr key={i} className="border-b border-background-200">
                          {row.map((cell, j) => (
                            <td key={j} className="py-3 px-4 text-foreground-600 text-xs">{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Cards */}
              {section.cards && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {section.cards.map((card, i) => (
                    <div key={i} className="bg-background-100 rounded-xl p-4">
                      <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center mb-3">
                        <i className={`${card.icon} text-accent-600 text-sm`}></i>
                      </div>
                      <p className="font-label font-semibold text-sm text-foreground-900 mb-1">{card.title}</p>
                      <p className="text-xs text-foreground-600">{card.description}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Pros & Cons */}
              {section.prosCons && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 my-6">
                  <div className="bg-accent-50/60 rounded-2xl p-5">
                    <p className="font-label font-semibold text-sm text-accent-700 mb-3 flex items-center gap-2">
                      <i className="ri-check-line"></i> Vantaggi / Cosa Fare
                    </p>
                    <ul className="space-y-2">
                      {section.prosCons.pros.map((item, i) => (
                        <li key={i} className="text-sm text-foreground-700 flex items-start gap-2">
                          <span className="text-accent-500 mt-1">+</span> {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-background-100 rounded-2xl p-5">
                    <p className="font-label font-semibold text-sm text-foreground-900 mb-3 flex items-center gap-2">
                      <i className="ri-information-line"></i> Limitazioni / Cosa Evitare
                    </p>
                    <ul className="space-y-2">
                      {section.prosCons.cons.map((item, i) => (
                        <li key={i} className="text-sm text-foreground-700 flex items-start gap-2">
                          <span className="text-foreground-400 mt-1">-</span> {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </section>
          ))}

          {/* Mid CTAs */}
          {article.ctas.slice(0, 1).map((cta, i) => (
            <div key={i} className="bg-foreground-950 rounded-2xl p-6 mb-10 text-center">
              <p className="text-background-50 text-lg font-heading font-bold mb-2">{cta.title}</p>
              <p className="text-background-50/70 text-sm mb-4">{cta.subtitle}</p>
              {cta.isExternal ? (
                <a
                  href={cta.linkUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-50 text-foreground-950 text-sm font-label font-semibold cursor-pointer whitespace-nowrap hover:bg-background-100 transition-colors"
                >
                  {cta.linkText} <i className="ri-arrow-right-line"></i>
                </a>
              ) : (
                <Link
                  to={cta.linkUrl}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-50 text-foreground-950 text-sm font-label font-semibold cursor-pointer whitespace-nowrap hover:bg-background-100 transition-colors"
                >
                  {cta.linkText} <i className="ri-arrow-right-line"></i>
                </Link>
              )}
            </div>
          ))}

          {/* Second CTA (if exists) */}
          {article.ctas.length > 1 && article.ctas.slice(1).map((cta, i) => (
            <div key={i} className="bg-background-100 border border-background-200 rounded-2xl p-6 mb-10 text-center">
              <p className="text-foreground-900 font-heading font-bold text-lg mb-2">{cta.title}</p>
              <p className="text-foreground-600 text-sm mb-4">{cta.subtitle}</p>
              {cta.isExternal ? (
                <a
                  href={cta.linkUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-foreground-950 text-background-50 text-sm font-label font-semibold cursor-pointer whitespace-nowrap hover:bg-foreground-800 transition-colors"
                >
                  {cta.linkText} <i className="ri-arrow-right-up-line"></i>
                </a>
              ) : (
                <Link
                  to={cta.linkUrl}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-foreground-950 text-background-50 text-sm font-label font-semibold cursor-pointer whitespace-nowrap hover:bg-foreground-800 transition-colors"
                >
                  {cta.linkText} <i className="ri-arrow-right-line"></i>
                </Link>
              )}
            </div>
          ))}

          {/* FAQ Section */}
          <section className="mb-10">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
              Domande Frequenti
            </h2>
            <div className="space-y-4">
              {article.faqs.map((faq, idx) => (
                <details key={idx} className="group bg-background-50 border border-background-200 rounded-xl overflow-hidden">
                  <summary className="px-5 py-4 cursor-pointer font-label font-medium text-sm text-foreground-800 flex items-center justify-between list-none">
                    {faq.q}
                    <i className="ri-add-line text-foreground-400 group-open:hidden"></i>
                    <i className="ri-subtract-line text-foreground-400 hidden group-open:inline"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* Related Links */}
          <section className="mb-10">
            <h3 className="font-heading font-semibold text-lg text-foreground-900 mb-4">Articoli Correlati</h3>
            <div className="flex flex-wrap gap-3">
              {article.relatedLinks.map((link, i) => (
                link.isExternal ? (
                  <a
                    key={i}
                    href={link.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-4 py-2 rounded-full border border-background-200 text-foreground-600 text-xs font-label cursor-pointer whitespace-nowrap hover:bg-background-100 transition-colors"
                  >
                    {link.text} <i className="ri-external-link-line ml-1"></i>
                  </a>
                ) : (
                  <Link
                    key={i}
                    to={link.url}
                    className="px-4 py-2 rounded-full border border-background-200 text-foreground-600 text-xs font-label cursor-pointer whitespace-nowrap hover:bg-background-100 transition-colors"
                  >
                    {link.text}
                  </Link>
                )
              ))}
            </div>
          </section>

          {/* Final CTA */}
          <div className="bg-foreground-950 rounded-2xl p-6 text-center mb-10">
            <p className="text-background-50 text-xl font-heading font-bold mb-2">
              Inizia Ora con Itaobuy Spreadsheet
            </p>
            <p className="text-background-50/70 text-sm mb-5">
              Esplora il database prodotti più completo per lo shopping online. Migliaia di articoli ti aspettano.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-3 rounded-full bg-background-50 text-foreground-950 text-sm font-label font-semibold cursor-pointer whitespace-nowrap hover:bg-background-100 transition-colors"
              >
                Itaobuy Sheet <i className="ri-arrow-right-up-line ml-1"></i>
              </a>
              <Link
                to="/"
                className="px-6 py-3 rounded-full border border-background-50/40 text-background-50 text-sm font-label font-medium cursor-pointer whitespace-nowrap hover:bg-background-50/10 transition-colors"
              >
                Torna alla Home
              </Link>
            </div>
          </div>

          {/* Article Footer Meta */}
          <div className="border-t border-background-200 pt-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 text-xs text-foreground-400 font-label">
            <div>
              <p>Ultimo aggiornamento: <strong className="text-foreground-600">{article.date}</strong></p>
              <p>Reviewed by: <strong className="text-foreground-600">{article.author}</strong></p>
            </div>
            <Link
              to="/"
              className="px-4 py-2 rounded-full border border-background-200 text-foreground-500 hover:text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-line mr-1.5"></i> Home
            </Link>
          </div>
        </div>
      </article>

      {/* Structured Data: BreadcrumbList Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: siteUrl,
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: article.title.split(':')[0].trim(),
                item: currentUrl,
              },
            ],
          }),
        }}
      />

      {/* Structured Data: FAQ Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: article.faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.q,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.a,
              },
            })),
          }),
        }}
      />

      {/* Structured Data: Article Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.description,
            url: currentUrl,
            author: { '@type': 'Organization', name: article.author, url: siteUrl },
            datePublished: '2026-07-01',
            dateModified: '2026-07-14',
            publisher: { '@type': 'Organization', name: 'itaobuyspread.cfd', url: siteUrl },
            mainEntityOfPage: { '@type': 'WebPage', '@id': currentUrl },
          }),
        }}
      />
    </main>
  );
}